import { useCallback, useEffect, useState, useRef } from 'react';
import { MonitorAlertFullDto, IntruderSnapshot } from '@/app/Alert/types';
import { Loader2 } from 'lucide-react';
import { cropImageToDataUrl } from '@/utils/imageUtils';

interface CroppedImageProps {
  coordinates?: IntruderSnapshot;
  alertData?: MonitorAlertFullDto;
  className?: string;
}

export function CroppedImage({ coordinates, alertData, className }: CroppedImageProps) {
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const lastEventValueIdRef = useRef<string | undefined>(undefined);

  const cropImage = useCallback(async (imageUrl: string, coords?: IntruderSnapshot) => {
    if (!coords) {
      setCroppedImage(imageUrl);
      return;
    }
    try {
      const result = await cropImageToDataUrl(imageUrl, coords);
      setCroppedImage(result);
    } catch (error) {
      console.error('Error cropping image:', error);
      setCroppedImage(imageUrl);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const eventValueId = coordinates?.eventValueId;

    // Only reset if the eventValueId actually changed to a different value
    const eventValueIdChanged = eventValueId !== lastEventValueIdRef.current;
    lastEventValueIdRef.current = eventValueId;

    // If we already have an image and eventValueId didn't change, skip reload
    if (croppedImage && !eventValueIdChanged) {
      return;
    }

    // Only show loading if we don't have an image yet
    if (!croppedImage) {
      setIsLoading(true);
    }

    const image =
      coordinates &&
      alertData?.events
        ?.find((e) => e.values?.find((v) => v.id === coordinates.eventValueId))
        ?.values?.find((v) => v.type === 'SnapshotImage')?.sourceUrl;

    if (image) {
      cropImage(image, coordinates);
    } else if (!croppedImage) {
      setIsLoading(false);
    }
  }, [coordinates?.eventValueId, alertData, cropImage, croppedImage]);

  if (!croppedImage) {
    return (
      <div
        className={`flex min-h-[50px] min-w-[50px] items-center justify-center bg-gray-900/20 ${className}`}
      >
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="relative flex h-full w-full items-center justify-center">
      <img src={croppedImage} alt="Cropped" className={className} />
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/20">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      )}
    </div>
  );
}
